import "./styles/AiExplorationCard.scss";

import { Card } from "@amzn/stencil-react-components/card";
import { Col, GridItem, GridItemProps } from "@amzn/stencil-react-components/layout";
import { LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import React, { FC } from "react";

/**
 * Grid span configuration for responsive layout
 */
type GridSpan = {
  lg: GridItemProps["lg"];
  md: GridItemProps["md"];
};

/**
 * Props for the AiExplorationCard component
 */
export interface AiExplorationCardProps {
  /** URL for the card media image */
  imageUrl: string;
  /** Alt text for the media image */
  imageAlt?: string;
  /** Bold title displayed below the image */
  title: string;
  /** Descriptive text displayed below the title */
  description: string;
  /** Optional URL — when provided the entire card becomes a clickable link */
  href?: string;
  /** Touchpoint identifier for tracking card interactions */
  touchpoint?: string;
  /** Responsive grid span configuration */
  gridSpan?: GridSpan;
  /** Index used for data attributes */
  index?: number;
}

/**
 * Card component for the Amazon AI Exploration section.
 * Displays a media image on top followed by a title and description.
 */
export const AiExplorationCard: FC<AiExplorationCardProps> = ({
  imageUrl,
  imageAlt = "",
  title,
  description,
  href,
  touchpoint,
  gridSpan,
  index = 0,
}) => {
  const cardContent = (
    <Col gridGap={16} style={{ minWidth: "100%" }}>
      {/* Media image */}
      <div className="ai-exploration-card__media">
        <img
          className="ai-exploration-card__media-image"
          src={imageUrl}
          alt={imageAlt}
          data-testid={`ai-exploration-card-image-${index}`}
        />
      </div>

      {/* Card header */}
      <Col gridGap={4}>
        <Text
          fontSize="T200"
          fontWeight="bold"
          data-testid={`ai-exploration-card-title-${index}`}
          style={{ wordBreak: "break-word" }}
        >
          {title}
        </Text>
        <Text fontSize="T100" data-testid={`ai-exploration-card-description-${index}`}>
          {description}
        </Text>
      </Col>
    </Col>
  );

  return (
    <GridItem lg={gridSpan?.lg ?? 6} md={gridSpan?.md ?? 4} data-testid="AiExplorationCard" data-index={index}>
      {href ? (
        <Link
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="ai-exploration-card__link-wrapper"
          aria-label={`${title} : ${description}`}
          touchpoint={touchpoint ?? "ai-exploration-card"}
          data-testid={`ai-exploration-card-link-${index}`}
          textDecoration={LinkTextDecorationOptions.None}
        >
          <Card style={{ minWidth: "100%" }} className="ai-exploration-card">
            {cardContent}
          </Card>
        </Link>
      ) : (
        <Card style={{ minWidth: "100%" }} className="ai-exploration-card">
          {cardContent}
        </Card>
      )}
    </GridItem>
  );
};
